import { useEffect, useState } from 'react';
import { secureRandom } from '@/lib/secureRandom';
import { BarChart3, Plus, Trash2 } from 'lucide-react';
import { useWorkforceStore, ServiceOrder, BenchTalent } from '../../../store/workforceStore';
import { Card, CardContent } from '../../../components/Card';
import { Badge } from '../../../components/Badge';
import { Button } from '../../../components/Button';
import { PageHeader } from '../../../components/PageHeader';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../../components/Dialog';
import toast from 'react-hot-toast';
import { Input } from '@/components/Input';

export function WorkforceDashboard() {
  const { 
    serviceOrders, 
    benchTalents, 
    fetchServiceOrders, 
    fetchBench, 
    createServiceOrder,
    addToBench,
    updateBenchTalent,
    deleteServiceOrder,
    deleteBenchTalent,
    getMatches
  } = useWorkforceStore();
  
  const [activeTab, setActiveTab] = useState<'orders' | 'bench' | 'matching'>('orders');
  const [selectedOrder, setSelectedOrder] = useState<ServiceOrder | null>(null);
  const [matches, setMatches] = useState<BenchTalent[]>([]);
  const [isMatching, setIsMatching] = useState(false);
  const [isAddingOrder, setIsAddingOrder] = useState(false);
  const [isAddingTalent, setIsAddingTalent] = useState(false);
  const [isEditingTalent, setIsEditingTalent] = useState(false);
  const [editingTalentId, setEditingTalentId] = useState<string | null>(null);
  const [deleteSOTarget, setDeleteSOTarget] = useState<{ id: string; label: string } | null>(null);
  const [deleteBenchTarget, setDeleteBenchTarget] = useState<{ id: string; label: string } | null>(null);
  const [busyDelete, setBusyDelete] = useState(false);

  const handleDeleteSO = async () => {
    if (!deleteSOTarget) return;
    setBusyDelete(true);
    try {
      await deleteServiceOrder(deleteSOTarget.id);
      toast.success('Service order deleted');
      setDeleteSOTarget(null);
    } catch {
      toast.error('Failed to delete service order');
    } finally {
      setBusyDelete(false);
    }
  };

  const handleDeleteBench = async () => {
    if (!deleteBenchTarget) return;
    setBusyDelete(true);
    try {
      await deleteBenchTalent(deleteBenchTarget.id);
      toast.success('Bench talent removed');
      setDeleteBenchTarget(null);
    } catch {
      toast.error('Failed to remove bench talent');
    } finally {
      setBusyDelete(false);
    }
  };
  
  const [newOrder, setNewOrder] = useState<Partial<ServiceOrder>>({
      clientName: '',
      projectName: '',
      positionTitle: '',
      requiredCount: 1,
      department: 'Engineering',
      mandatorySkills: [],
      priority: 'medium',
      expectedDate: new Date().toISOString().split('T')[0]
  });

  const [newTalent, setNewTalent] = useState<Partial<BenchTalent>>({
      name: '',
      designation: '',
      totalExperience: 0,
      primarySkills: [],
      secondarySkills: [],
      previousProjects: [],
      availabilityDate: new Date().toISOString().split('T')[0],
      onBenchSince: new Date().toISOString().split('T')[0],
      costRate: 0,
      performanceScore: 5,
      resumeUrl: ''
  });

  useEffect(() => {
    fetchServiceOrders();
    fetchBench();
  }, []);

  const handleRunMatching = async (so: ServiceOrder) => {
    setIsMatching(true);
    setSelectedOrder(so);
    setActiveTab('matching');
    const results = await getMatches(so.id);
    setMatches(results);
    setIsMatching(false);
    if (results.length === 0) {
      toast.error('No internal bench matches found. Routing to TA team.');
    } else {
      toast.success(`${results.length} internal candidates matched!`);
    }
  };

  const calculateBenchDuration = (since: string) => {
    const start = new Date(since || Date.now());
    const end = new Date();
    const diff = Math.floor((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24));
    return `${diff} Days`;
  };

  const openEditModal = (talent: BenchTalent) => {
      setNewTalent({ ...talent });
      setEditingTalentId(talent.id);
      setIsEditingTalent(true);
  };

  const handleSaveTalent = async () => {
      if (!newTalent.name?.trim()) { toast.error('Talent name is required'); return; }
      if (!newTalent.designation?.trim()) { toast.error('Designation is required'); return; }
      if (Number(newTalent.totalExperience) < 0) { toast.error('Experience cannot be negative'); return; }
      if (isEditingTalent && editingTalentId) {
          await updateBenchTalent(editingTalentId, newTalent);
          toast.success('Profile updated successfully!');
      } else {
          await addToBench(newTalent);
          toast.success(`${newTalent.name} added to bench!`);
      }
      setIsAddingTalent(false);
      setIsEditingTalent(false);
      setEditingTalentId(null);
      // Reset form
      setNewTalent({ name: '', designation: '', totalExperience: 0, primarySkills: [], secondarySkills: [], previousProjects: [], availabilityDate: new Date().toISOString().split('T')[0], onBenchSince: new Date().toISOString().split('T')[0], costRate: 0, performanceScore: 5, resumeUrl: '' });
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="Workforce Fulfillment"
        description="Service order → bench match → hiring lifecycle."
        actions={
          <>
            <Button variant="secondary" size="sm"><BarChart3 /> Reports</Button>
            <Button size="sm" onClick={() => setIsAddingOrder(true)}><Plus /> Raise Demand</Button>
          </>
        }
      />

      <Dialog open={isAddingOrder} onOpenChange={setIsAddingOrder}>
          <DialogContent className="max-w-2xl">
                  <div className="space-y-6">
                      <DialogHeader>
                          <DialogTitle>Raise Resource Demand</DialogTitle>
                      </DialogHeader>

                      <div className="grid grid-cols-2 gap-4">
                          <div className="space-y-2">
                              <label className="text-xs font-semibold text-muted-foreground">Client Name</label>
                              <Input 
                                  className="w-full bg-background border border-border rounded-lg p-3 text-sm text-foreground"
                                  placeholder="e.g. Acme Corp"
                                  onChange={e => setNewOrder({...newOrder, clientName: e.target.value})}
                              />
                          </div>
                           <div className="space-y-2">
                              <label className="text-xs font-semibold text-muted-foreground">Position Title</label>
                              <Input 
                                  className="w-full bg-background border border-border rounded-lg p-3 text-sm text-foreground"
                                  placeholder="e.g. Lead Dev"
                                  onChange={e => setNewOrder({...newOrder, positionTitle: e.target.value})}
                              />
                          </div>
                          <div className="space-y-2">
                              <label className="text-xs font-semibold text-muted-foreground">Skills (comma separated)</label>
                              <Input 
                                  className="w-full bg-background border border-border rounded-lg p-3 text-sm text-foreground"
                                  placeholder="Java, Python..."
                                  onChange={e => setNewOrder({...newOrder, mandatorySkills: e.target.value.split(',').map(s => s.trim())})}
                              />
                          </div>
                          <div className="space-y-2">
                              <label className="text-xs font-semibold text-muted-foreground">Exp. Required</label>
                              <Input 
                                  className="w-full bg-background border border-border rounded-lg p-3 text-sm text-foreground"
                                  placeholder="e.g. 5 Years"
                                  onChange={e => setNewOrder({...newOrder, experienceRequired: e.target.value})}
                              />
                          </div>
                      </div>

                      <div className="flex gap-4 pt-4">
                          <Button variant="secondary" className="flex-1" onClick={() => setIsAddingOrder(false)}>Cancel</Button>
                          <Button variant="primary" className="flex-1" onClick={async () => {
                              if (!newOrder.clientName?.trim()) { toast.error('Client name is required'); return; }
                              if (!newOrder.positionTitle?.trim()) { toast.error('Position title is required'); return; }
                              if (Number(newOrder.requiredCount) < 1) { toast.error('Required count must be at least 1'); return; }
                              await createServiceOrder({...newOrder, soId: `SO-${Math.floor(1000 + secureRandom() * 9000)}`});
                              setIsAddingOrder(false);
                              toast.success('Resource demand raised! Auto-matching session initiated.');
                          }}>Submit Service Order</Button>
                      </div>
                  </div>
          </DialogContent>
      </Dialog>

      {/* Stats Overview */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        {[
          { label: 'Open Demands', value: serviceOrders.length, icon: '', color: 'primary' },
          { label: 'Bench Available', value: benchTalents.filter(t => t.status === 'available').length, icon: '', color: 'success' },
          { label: 'Fulfillment Rate', value: '0%', icon: '', color: 'info' },
          { label: 'Avg Time to Hire', value: '0 Days', icon: '', color: 'warning' },
        ].map((stat, i) => (
          <Card key={i} className="bg-card border-border">
            <CardContent className="p-5">
              <div className="flex justify-between items-start">
                <div>
                  <p className="text-xs font-semibold text-muted-foreground">{stat.label}</p>
                  <p className="text-foreground mt-1 font-display text-metric">{stat.value}</p>
                </div>
                <span className="text-2xl opacity-50">{stat.icon}</span>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Main Navigation Tabs */}
      <div className="flex gap-6 border-b border-border">
        {[
          { id: 'orders', label: 'Service Orders' },
          { id: 'bench', label: 'Bench Management' },
          { id: 'matching', label: 'Talent Matching' },
        ].map(tab => (
          <Button variant="unstyled" size="unstyled"
            key={tab.id}
            onClick={() => setActiveTab(tab.id as any)}
            className={`pb-4 text-xs font-semibold transition-all relative ${
              activeTab === tab.id ? 'text-primary' : 'text-muted-foreground hover:text-muted-foreground'
            }`}
          >
            {tab.label}
            {activeTab === tab.id && (
              <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary rounded-full" />
            )}
          </Button>
        ))}
      </div>

      {/* Tab Content */}
      <div className="animate-in fade-in slide-in-from-bottom-2 duration-300">
        {activeTab === 'orders' && (
          <div className="space-y-4">
            {serviceOrders.length > 0 ? serviceOrders.map(so => (
              <Card key={so.id} className="hover:border-primary/30 transition-all border-border bg-card/10">
                <CardContent className="p-6">
                  <div className="flex flex-col md:flex-row justify-between gap-6">
                    <div className="space-y-3 flex-1">
                      <div className="flex items-center gap-3">
                        <span className="text-xs font-semibold text-primary">{so.soId}</span>
                        <h3 className="text-foreground font-display text-section-title">{so.positionTitle}</h3>
                        <span className={`text-xs font-semibold px-2 py-0.5 rounded border ${so.priority === 'critical' ? 'border-destructive/20 text-destructive bg-destructive/10' : 'border-warning/20 text-warning bg-warning/10'}`}>{so.priority}</span>
                      </div>
                      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 text-xs">
                        <div>
                          <p className="text-muted-foreground font-semibold">Client / Project</p>
                          <p className="text-foreground mt-1 font-semibold">{so.clientName || 'N/A'} / {so.projectName || 'N/A'}</p>
                        </div>
                        <div>
                          <p className="text-muted-foreground font-semibold">Department</p>
                          <p className="text-foreground mt-1 font-semibold">{so.department}</p>
                        </div>
                        <div>
                          <p className="text-muted-foreground font-semibold">Required Date</p>
                          <p className="text-foreground mt-1 font-semibold">{new Date(so.expectedDate).toLocaleDateString()}</p>
                        </div>
                        <div>
                          <p className="text-muted-foreground font-semibold">Status</p>
                          <p className="text-primary font-semibold mt-1">{so.status}</p>
                        </div>
                      </div>
                      <div className="flex flex-wrap gap-2 pt-2">
                        {so.mandatorySkills.map(s => (
                          <span key={s} className="px-2 py-0.5 bg-primary/10 border border-primary/20 rounded text-xs text-primary font-semibold">
                            {s}
                          </span>
                        ))}
                      </div>
                    </div>
                    <div className="flex flex-col justify-center gap-2 w-full md:w-auto">
                      <Button variant="primary" size="sm" onClick={() => handleRunMatching(so)}>
                        Run Match Engine
                      </Button>
                      <Button variant="secondary" size="sm" className="text-destructive"
                        onClick={() => setDeleteSOTarget({ id: so.id, label: `${so.soId} · ${so.positionTitle}` })}>
                        <Trash2 className="w-4 h-4 mr-1" /> Delete
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )) : (
              <Card className="border-dashed border-border">
                <CardContent className="p-8 text-center">
                  <p className="text-lg font-semibold text-foreground">No service orders yet</p>
                  <p className="mt-2 text-sm text-muted-foreground">Resource demands will appear here once the backend sends them.</p>
                </CardContent>
              </Card>
            )}
          </div>
        )}

        {activeTab === 'bench' && (
          <div className="space-y-6">
            <div className="flex justify-between items-center">
                 <p className="text-xs font-semibold text-muted-foreground">All Internal Bench Resources</p>
                 <Button variant="primary" size="sm" onClick={() => setIsAddingTalent(true)}>+ Add Talent to Bench</Button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {benchTalents.length > 0 ? benchTalents.map(talent => (
                <Card key={talent.id} className="border-border hover:border-primary/30 transition-all bg-card/10 group">
                    <CardContent className="p-6 space-y-4">
                    <div className="flex items-center gap-4">
                        <div className="w-14 h-14 rounded-lg bg-background border border-border flex items-center justify-center text-2xl group-hover:scale-110 transition-transform">
                                                  </div>
                        <div className="flex-1">
                            <div className="flex justify-between items-start gap-2">
                                <h4 className="text-foreground text-sm font-semibold">{talent.name}</h4>
                                <div className="flex items-center gap-2 shrink-0">
                                    <Button variant="unstyled" size="unstyled" onClick={() => openEditModal(talent)} className="text-xs font-semibold text-primary hover:text-primary">Edit</Button>
                                    <button type="button" aria-label={`Remove ${talent.name}`}
                                        onClick={() => setDeleteBenchTarget({ id: talent.id, label: talent.name })}
                                        className="text-muted-foreground hover:text-destructive">
                                        <Trash2 className="w-3.5 h-3.5" />
                                    </button>
                                </div>
                            </div>
                            <p className="text-xs text-muted-foreground font-semibold leading-none mt-1">{talent.designation}</p>
                        </div>
                    </div>
                    <div className="grid grid-cols-2 gap-y-4 gap-x-6 text-xs">
                        <div>
                            <p className="text-muted-foreground font-semibold opacity-70">Experience</p>
                            <p className="text-foreground font-semibold mt-0.5 tracking-tight">{Number.isNaN(talent.totalExperience) ? '0' : talent.totalExperience} Years</p>
                        </div>
                        <div>
                            <p className="text-muted-foreground font-semibold opacity-70">Status</p>
                            <Badge variant={talent.status === 'available' ? 'success' : 'warning'} className="text-xs py-0 mt-0.5">{talent.status}</Badge>
                        </div>
                        <div>
                            <p className="text-muted-foreground font-semibold opacity-70">Sustainability</p>
                            <p className="text-foreground font-semibold mt-0.5 tracking-tight">{calculateBenchDuration(talent.onBenchSince || talent.availabilityDate)}</p>
                        </div>
                        <div>
                            <p className="text-muted-foreground font-semibold opacity-70">Perf. Score</p>
                            <p className="text-info font-semibold mt-0.5">{talent.performanceScore}/5</p>
                        </div>
                    </div>
                    <div className="flex flex-wrap gap-1.5 pt-2">
                        {talent.primarySkills.map(s => (
                        <span key={s} className="px-1.5 py-0.5 bg-background border border-border rounded text-xs text-muted-foreground font-semibold">
                            {s}
                        </span>
                        ))}
                    </div>
                    <div className="flex gap-2">
                        <Button variant="secondary" size="sm" className="flex-1 text-xs font-semibold">View Full Profile</Button>
                        {talent.resumeUrl && <Button variant="secondary" size="sm" className="w-10 flex items-center justify-center"></Button>}
                    </div>
                    </CardContent>
                </Card>
                )) : (
                  <Card className="border-dashed border-border md:col-span-2 lg:col-span-3">
                    <CardContent className="p-8 text-center">
                      <p className="text-lg font-semibold text-foreground">No bench talent yet</p>
                      <p className="mt-2 text-sm text-muted-foreground">Bench profiles will show here when records are available.</p>
                    </CardContent>
                  </Card>
                )}
            </div>

            <Dialog open={isAddingTalent || isEditingTalent} onOpenChange={(open) => { if (!open) { setIsAddingTalent(false); setIsEditingTalent(false); setEditingTalentId(null); } }}>
                <DialogContent className="max-w-2xl">
                        <div className="space-y-6">
                            <DialogHeader>
                                <DialogTitle>{isEditingTalent ? 'Edit Talent Profile' : 'Move Employee to Bench'}</DialogTitle>
                                <DialogDescription>Operational skills & resource inventory</DialogDescription>
                            </DialogHeader>

                            <div className="grid grid-cols-2 gap-x-6 gap-y-4">
                                <div className="space-y-1">
                                    <label className="text-xs font-semibold text-muted-foreground">Employee Name</label>
                                    <Input 
                                        className="w-full bg-background border border-border rounded-lg p-3 text-sm text-foreground focus:outline-none focus:border-primary" 
                                        value={newTalent.name}
                                        placeholder="Current Employee Name"
                                        onChange={e => setNewTalent({...newTalent, name: e.target.value})}
                                    />
                                </div>
                                <div className="space-y-1">
                                    <label className="text-xs font-semibold text-muted-foreground">Designation</label>
                                    <Input 
                                        className="w-full bg-background border border-border rounded-lg p-3 text-sm text-foreground focus:outline-none focus:border-primary" 
                                        value={newTalent.designation}
                                        placeholder="Senior Developer, etc."
                                        onChange={e => setNewTalent({...newTalent, designation: e.target.value})}
                                    />
                                </div>
                                <div className="space-y-1">
                                    <label className="text-xs font-semibold text-muted-foreground">Primary Skills (Comma separated)</label>
                                    <Input 
                                        className="w-full bg-background border border-border rounded-lg p-3 text-sm text-foreground focus:outline-none focus:border-primary" 
                                        value={newTalent.primarySkills?.join(', ')}
                                        placeholder="React, Node.js..."
                                        onChange={e => setNewTalent({...newTalent, primarySkills: e.target.value.split(',').map(s => s.trim())})}
                                    />
                                </div>
                                <div className="space-y-1">
                                    <label className="text-xs font-semibold text-muted-foreground">Total Experience (Years)</label>
                                    <Input 
                                        type="number"
                                        className="w-full bg-background border border-border rounded-lg p-3 text-sm text-foreground" 
                                        value={newTalent.totalExperience || 0}
                                        onChange={e => setNewTalent({...newTalent, totalExperience: parseInt(e.target.value) || 0})}
                                    />
                                </div>
                                <div className="space-y-1 col-span-2">
                                    <label className="text-xs font-semibold text-muted-foreground">Previous Project Details (Experience Narrative)</label>
                                    <textarea 
                                        className="w-full bg-background border border-border rounded-lg p-3 text-sm text-foreground min-h-[80px]" 
                                        value={newTalent.previousProjects?.join('\n')}
                                        placeholder="e.g. Led cloud migration at Acme Corp..."
                                        onChange={e => setNewTalent({...newTalent, previousProjects: e.target.value.split('\n')})}
                                    />
                                </div>
                                <div className="space-y-1">
                                    <label className="text-xs font-semibold text-muted-foreground">Benchmark Since</label>
                                    <Input 
                                        type="date"
                                        className="w-full bg-background border border-border rounded-lg p-3 text-sm text-foreground" 
                                        value={newTalent.onBenchSince?.split('T')[0]}
                                        onChange={e => setNewTalent({...newTalent, onBenchSince: e.target.value})}
                                    />
                                </div>
                                <div className="space-y-1">
                                    <label className="text-xs font-semibold text-muted-foreground">Resume Upload (Mock API)</label>
                                    <div className="flex gap-2">
                                        <Input 
                                            type="text"
                                            className="flex-1 bg-background border border-border rounded-lg p-3 text-xs text-muted-foreground" 
                                            placeholder="Upload Result Link..."
                                            value={newTalent.resumeUrl}
                                            onChange={e => setNewTalent({...newTalent, resumeUrl: e.target.value})}
                                        />
                                        <Button variant="secondary" size="sm" onClick={() => toast.success('File Browser opened.')}>Browse</Button>
                                    </div>
                                </div>
                            </div>

                            <div className="pt-6 flex gap-3">
                                <Button variant="secondary" className="flex-1 rounded-lg" onClick={() => { setIsAddingTalent(false); setIsEditingTalent(false); setEditingTalentId(null); }}>Discard</Button>
                                <Button 
                                    variant="primary" 
                                    className="flex-1 rounded-lg font-semibold" 
                                    onClick={handleSaveTalent}
                                >
                                    {isEditingTalent ? 'Save Profile' : 'Confirm Bench Move'}
                                </Button>
                            </div>
                        </div>
                </DialogContent>
            </Dialog>
          </div>
        )}

        {activeTab === 'matching' && selectedOrder && (
          <div className="space-y-6">
            <div className="p-8 bg-card rounded-lg border border-primary/30 flex justify-between items-center">
              <div>
                <p className="text-xs font-semibold text-primary tracking-[0.4em]">Intelligence Match Pool</p>
                <h2 className="text-foreground mt-1 font-display text-section-title">{selectedOrder.positionTitle}</h2>
                <div className="flex gap-4 mt-2">
                  <span className="text-xs font-semibold text-muted-foreground">SO_ID: {selectedOrder.soId}</span>
                  <span className="text-xs text-muted-foreground">•</span>
                  <span className="text-xs font-semibold text-muted-foreground">{selectedOrder.projectName}</span>
                </div>
              </div>
              <Button variant="secondary" size="sm" onClick={() => setSelectedOrder(null)}>Back to Orders</Button>
            </div>

            {isMatching ? (
              <div className="flex flex-col items-center justify-center h-64 space-y-4">
                <div className="w-16 h-16 rounded-full border-4 border-primary/10 border-t-primary-500 animate-spin" />
                <p className="text-muted-foreground text-xs font-semibold tracking-[0.3em] animate-pulse">Calculating Resource Affinity...</p>
              </div>
            ) : matches.length > 0 ? (
              <div className="grid grid-cols-1 gap-4">
                <p className="text-xs font-semibold text-success ml-2">Proprietary Match Logic Found {matches.length} Internal Candidates</p>
                {matches.map(m => (
                  <Card key={m.id} className="border-success/20 hover:border-success/50 transition-all bg-success/5 group">
                    <CardContent className="p-6 flex flex-col md:flex-row justify-between items-center gap-6">
                      <div className="flex items-center gap-6">
                        <div className="w-14 h-14 rounded-lg bg-success/20 flex items-center justify-center text-2xl group-hover:rotate-12 transition-transform"></div>
                        <div>
                          <div className="flex items-center gap-3">
                                <h4 className="text-foreground text-sm font-semibold">{m.name}</h4>
                                <Badge variant="success" className="text-xs px-2 py-0">98% Fit</Badge>
                          </div>
                          <p className="text-xs text-muted-foreground font-semibold mt-1">{m.designation}</p>
                          <div className="flex gap-4 mt-2 text-xs font-semibold text-muted-foreground">
                            <span>Available: {new Date(m.availabilityDate).toLocaleDateString()}</span>
                            <span>Bench Sustainability: {calculateBenchDuration(m.onBenchSince)}</span>
                          </div>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <Button variant="secondary" size="sm">Compare</Button>
                        <Button variant="success" size="sm" onClick={() => {
                            useWorkforceStore.getState().allocateTalent(selectedOrder.id, m.id);
                            toast.success(`Request sent to ${m.name}'s manager for allocation!`);
                        }}>Allocate Candidate</Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : (
              <Card className="p-12 text-center border-dashed border-destructive/30 bg-destructive/5 rounded-lg">
                <p className="text-4xl mb-6"></p>
                <h3 className="text-destructive font-display text-metric">Negative Match Coverage</h3>
                <p className="text-muted-foreground text-xs font-semibold mt-3 mb-10 max-w-sm mx-auto leading-loose">
                    All internal candidates have missing mandatory affinity or are currently deep-allocated.
                </p>
                <div className="flex justify-center gap-4">
                  <Button variant="primary" onClick={() => toast.success('Position published to ATS Dashboard!')}>
                    Route to Talent Sourcing Team
                  </Button>
                  <Button variant="secondary">Modify SO Specs</Button>
                </div>
              </Card>
            )}
          </div>
        )}
      </div>

      {/* Delete service order confirmation */}
      <Dialog open={!!deleteSOTarget} onOpenChange={(o) => !o && setDeleteSOTarget(null)}>
        <DialogContent className="max-w-[440px]">
          <DialogHeader>
            <DialogTitle>Delete service order</DialogTitle>
            <DialogDescription>
              Delete <span className="font-medium text-foreground">{deleteSOTarget?.label}</span>? This cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="secondary" onClick={() => setDeleteSOTarget(null)} disabled={busyDelete}>Cancel</Button>
            <Button variant="destructive" onClick={handleDeleteSO} isLoading={busyDelete}>Delete</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete bench talent confirmation */}
      <Dialog open={!!deleteBenchTarget} onOpenChange={(o) => !o && setDeleteBenchTarget(null)}>
        <DialogContent className="max-w-[440px]">
          <DialogHeader>
            <DialogTitle>Remove bench talent</DialogTitle>
            <DialogDescription>
              Remove <span className="font-medium text-foreground">{deleteBenchTarget?.label}</span> from the bench? This cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="secondary" onClick={() => setDeleteBenchTarget(null)} disabled={busyDelete}>Cancel</Button>
            <Button variant="destructive" onClick={handleDeleteBench} isLoading={busyDelete}>Remove</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

